/**
 * Hidden tabs (#1809, WS_DESIGN_1809 W12). A tab hidden for HIDDEN_CLOSE_MS holds no socket and sends no poll. Hidden less
 * than that, nothing changes. On return the sockets reopen at once and re-sync once:
 *
 *   catalog         the reconnect read of useHoldOutcomeMeta, with the replay
 *   balances etc.   the first frame of each stream. The order ticket stays blocked until balances have one, or REST after
 *                   the session's usual wait (userSession.ts)
 *   fills, history  the overlap check of the snapshot (sessionRows.ts)
 *   volume          one read (useHoldDayVolumes)
 *   toasts          one summary for the whole time away (lifecycle.ts)
 *
 * Why it closes: past 5 minutes Chrome runs a hidden tab's timers about once a minute, close to Hyperliquid's 60 s cutoff, so
 * a ping is unreliable, and phones stop timers altogether. So the 5 minutes also fit the throttling. Each socket closed is
 * one of the 10 Hyperliquid allows per IP, handed to the tabs in use.
 *
 * - Limits: only a tab in front reopens, on the tab's ConnectBudget, and a tab cannot close again for 5 minutes short of a
 *   freeze or a bfcache hit, so tabs left in the back add nothing. Reads go through the background pacer.
 * - A timer may never have run: a phone stops the page. So a return after HIDDEN_CLOSE_MS without a close does the close
 *   there and then, and the socket found on return, perhaps dead, is replaced the same way.
 * - Health and the status dot do not see any of it (WsSockets.state, HealthMonitor.noteReturn).
 * - Never while an order is in flight, wallet prompt included (holdSocketsOpen): the close waits for its end.
 * - `freeze` and a `pagehide` that may enter the bfcache close at once, however long the tab was hidden: a frozen page cannot
 *   answer a socket, and an open socket can keep the page out of the bfcache. `pageshow`, `resume` and `focus` count as a
 *   return when the tab is visible, in case a browser skips visibilitychange: a tab must not stay asleep in front of the user.
 *   A `pagehide` that is not persisted is the end of the page.
 *
 * window.__hip4Ws holds the counters, for the real-browser test.
 */
import type { WsCounters } from './ws';

/** How long a tab stays hidden before its sockets close. */
export const HIDDEN_CLOSE_MS = 5 * 60_000;

/** Test seam (e2e): `window.__hip4HiddenCloseMs = 500` before the app loads. */
const OVERRIDE_KEY = '__hip4HiddenCloseMs';

export interface TabCounters extends WsCounters {
  /** Hidden time of finished hides. */
  hiddenMs: number;
  /** Times the tab went to sleep and came back. */
  sleeps: number;
  wakes: number;
  /** Closes put off because an order was in flight. */
  heldCloses: number;
  asleep: boolean;
}

declare global {
  interface Window {
    /** Socket counters of this tab, for a look in the console (#1809). */
    __hip4Ws?: TabCounters;
  }
}

/** The tab's counters. One page, one set, across env switches. */
export const wsCounters: TabCounters = {
  closes: 0,
  reopens: 0,
  missedPongs: 0,
  lastReopenMs: 0,
  hiddenMs: 0,
  sleeps: 0,
  wakes: 0,
  heldCloses: 0,
  asleep: false,
};

/** This tab holds no sockets now. */
export function tabAsleep(): boolean {
  return wsCounters.asleep;
}

let holds = 0;
const releaseListeners = new Set<() => void>();

/** Keeps the sockets open until the returned function is called: an order or cancel is on its way. */
export function holdSocketsOpen(): () => void {
  holds++;
  let done = false;
  return () => {
    if (done) return;
    done = true;
    holds--;
    if (holds === 0) for (const cb of [...releaseListeners]) cb();
  };
}

export interface SleepingSockets {
  sleep(): void;
  wake(hiddenSince: number): void;
}

export interface TabSleepOptions {
  closeMs?: number;
  counters?: TabCounters;
  now?: () => number;
}

function closeMsOverride(): number | undefined {
  try {
    const v = (globalThis as Record<string, unknown>)[OVERRIDE_KEY];
    return typeof v === 'number' ? v : undefined;
  } catch {
    return undefined;
  }
}

const isHidden = () => document.visibilityState === 'hidden';

export class TabSleep {
  private readonly closeMs: number;
  private readonly counters: TabCounters;
  private readonly now: () => number;
  private hiddenAt: number | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly sockets: SleepingSockets,
    private readonly health: { noteReturn(): void },
    opts: TabSleepOptions = {},
  ) {
    this.closeMs = opts.closeMs ?? closeMsOverride() ?? HIDDEN_CLOSE_MS;
    this.counters = opts.counters ?? wsCounters;
    this.now = opts.now ?? Date.now;
  }

  start(): () => void {
    const onVisibility = () => (isHidden() ? this.onHidden() : this.onReturn());
    const onFreeze = () => this.sleep();
    const onPageHide = (e: PageTransitionEvent) => {
      if (e.persisted) this.sleep();
    };
    const onBack = () => {
      if (!isHidden()) this.onReturn();
    };
    const onRelease = () => {
      if (this.hiddenAt !== null && this.now() - this.hiddenAt >= this.closeMs) this.sleep();
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('freeze', onFreeze);
    document.addEventListener('resume', onBack);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onBack);
    window.addEventListener('focus', onBack);
    releaseListeners.add(onRelease);
    window.__hip4Ws = this.counters;
    if (isHidden()) this.onHidden();
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('freeze', onFreeze);
      document.removeEventListener('resume', onBack);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onBack);
      window.removeEventListener('focus', onBack);
      releaseListeners.delete(onRelease);
      this.stopTimer();
      this.counters.asleep = false;
    };
  }

  private onHidden(): void {
    if (this.hiddenAt !== null) return;
    this.hiddenAt = this.now();
    this.timer = setTimeout(() => {
      this.timer = null;
      this.sleep();
    }, this.closeMs);
  }

  private sleep(): void {
    if (this.counters.asleep) return;
    if (holds > 0) {
      this.counters.heldCloses++;
      return;
    }
    this.hiddenAt ??= this.now();
    this.stopTimer();
    this.counters.asleep = true;
    this.counters.sleeps++;
    this.sockets.sleep();
  }

  private onReturn(): void {
    this.stopTimer();
    const since = this.hiddenAt;
    if (since === null) return;
    this.hiddenAt = null;
    const hiddenFor = this.now() - since;
    this.counters.hiddenMs += hiddenFor;
    this.health.noteReturn();
    if (hiddenFor >= this.closeMs) this.sleep();
    if (!this.counters.asleep) return;
    this.counters.asleep = false;
    this.counters.wakes++;
    this.sockets.wake(since);
  }

  private stopTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}
